import { useMutation } from '@provision/graphql-client';
import { BackgroundJob } from 'src/graphql/entities/background-job/background-job.types';
import { START_DTOOLS_PROJECT_IMPORT_JOB } from 'src/graphql/entities/integrations/dtools/dtools.operations';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

interface StartDtoolsProjectImportJobVars {
  params: {
    partnerId: number;
    projectIds: string[];
    dryRun: boolean;
  };
}

/**
 * Starts a background import of D-Tools projects for the selected partner. With `dryRun` the job
 * only builds the created/updated preview. Resolves to the started job, or null on failure (the
 * error is shown to the user).
 */
export const useStartDtoolsProjectImportJob = () => {
  const { showErrorAlert } = useNotifications();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const [startJobMutation, { loading }] = useMutation<
    { startDtoolsProjectImportJob: BackgroundJob },
    StartDtoolsProjectImportJobVars
  >(START_DTOOLS_PROJECT_IMPORT_JOB, {
    onError: (error) => showErrorAlert(error.message),
  });

  const startJob = async (input: {
    projectIds: string[];
    dryRun: boolean;
  }): Promise<BackgroundJob | null> => {
    if (selectedPartnerId === null || selectedPartnerId === undefined) {
      return null;
    }

    const { data } = await startJobMutation({
      variables: {
        params: {
          partnerId: selectedPartnerId,
          projectIds: input.projectIds,
          dryRun: input.dryRun,
        },
      },
    });

    return data?.startDtoolsProjectImportJob ?? null;
  };

  return { startJob, loading };
};
